import axios, { AxiosInstance, AxiosResponse } from "axios";
import ERROR_MESSAGE from "constants/message";
import { getUserStorage, clearAllStorage } from "util/local_storage";
import { API_BASE_URL } from "config";
import Timers from "util/timers";
import store from "store";
import { commonActions } from "store/common";
import isTokenExpired from "util/check_token_expiration";
import Notify from "./notify";

interface ResponseType {
  status: number;
  message?: string;
}

interface FetchResponse<T> extends ResponseType {
  data?: T;
}

class ApiClient {
  private axiosInstance: AxiosInstance;

  private token: string | null = null;

  private refreshToken: string | null = null;

  private maxRetries: number = 0;

  private retryDelay: number = 100;

  constructor(baseURL: string = API_BASE_URL || "") {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: this.getHeaders(),
    });

    // Interceptor for requests to add token
    this.axiosInstance.interceptors.request.use((config) => {
      const newConfig = config;
      if (this.token) {
        newConfig.headers.Authorization = `Bearer ${this.token}`;
      }
      return newConfig;
    });
  }

  public setToken(token: string, refreshToken: string = ""): void {
    this.token = token;
    this.refreshToken = refreshToken;
  }

  public clearToken(): void {
    this.token = null;
    this.refreshToken = null;
  }

  private handleForbiddenError(data?: any) {
    const { pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);

    const isQuotationPath = pathname.startsWith("/quotation/");
    const isQrRead = searchParams.get("qr_read") === "yes";

    if (isQuotationPath && isQrRead) {
      // TODO: Replace with the actual path for the QR read
      window.location.href = "/mst-car-model/result-pdf?showPdf=true";
      return;
    }

    const message =
      typeof data?.message === "object"
        ? data.message.message
        : data?.message || ERROR_MESSAGE.FORBIDDEN.MESSAGE;

    // Show notification (Notify is guaranteed to be initialized when API is called)
    Notify.setNotification({
      show: true,
      title: ERROR_MESSAGE.FORBIDDEN.TITLE,
      message: ERROR_MESSAGE.FORBIDDEN.MESSAGE,
    });

    // Set onClose callback to clear storage and redirect
    Notify.onClose = () => {
      this.clearToken();
      clearAllStorage();
      window.location.href = "/login";
    };
  }

  private handleError(response: AxiosResponse & { message?: string }): void {
    const { status, data } = response || {};

    switch (status) {
      case 403:
        this.handleForbiddenError(data);
        break;
      case 400:
      case 404:
      case 500:
        if (data?.message) {
          Notify?.setNotification?.({
            show: true,
            title: typeof data?.message === "object" ? data?.message.message : data?.message,
            message: "",
            titleColor: "var(--error)",
          });
        }
        break;
      case 401:
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          this.clearToken();
          clearAllStorage();
          window.location.href = "/login";
        };
        break;
      default:
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.DEFAULT.TITLE,
          message: ERROR_MESSAGE.DEFAULT.MESSAGE,
        });
        break;
    }
  }

  private getHeaders(headerCustom: Record<string, string> = {}): Record<string, string> {
    const token = this.token || getUserStorage()?.accessToken;
    return {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
      ...headerCustom,
    };
  }

  private async request<T>(
    endpoint: string,
    method: string,
    body?: any,
    headers: Record<string, string> = {},
    params?: Record<string, string>,
  ): Promise<FetchResponse<T>> {
    // Skip token checking for login and refresh token APIs
    const isLoginApi = endpoint === "/mst/user/auth/login";
    const isRefreshTokenApi = endpoint === "/mst/user/auth/update/token";
    const shouldCheckToken = !isLoginApi && !isRefreshTokenApi;

    if (shouldCheckToken) {
      // Check if token is still valid (not expired)
      if (!isTokenExpired()) {
        // Token is still valid, refresh token before calling API
        const { default: refreshToken } = await import("util/tokenRefresh");
        await refreshToken();

        // Wait for any ongoing token refresh to complete before making API calls
        const { waitForTokenRefresh } = await import("util/tokenRefresh");
        await waitForTokenRefresh();
      }
      // If token has expired, continue to call API with old token
      // Backend will return 403, then handleError will show notification
      // At that time, Notify component has already been initialized
    }

    let attempt = 0;
    let errorEnd: any = null;
    const headersWithToken = this.getHeaders(headers);

    // Don't show loading indicator for token refresh API
    if (!isRefreshTokenApi) {
      store.dispatch(commonActions.setLoading());
    }

    try {
      while (attempt <= this.maxRetries) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response: AxiosResponse<T> = await this.axiosInstance.request({
            url: endpoint,
            method,
            data: body,
            headers: headersWithToken,
            params,
          });

          return {
            status: response.status,
            data: response.data,
          };
        } catch (error: any) {
          errorEnd = error;
          attempt += 1;
          if (attempt > this.maxRetries) {
            this.handleError(error?.response);
            throw error;
          }

          // eslint-disable-next-line no-await-in-loop
          await Timers.delay(this.retryDelay);
        }
      }
    } catch (error: any) {
      this.handleError(error?.response);
    } finally {
      if (!isRefreshTokenApi) {
        store.dispatch(commonActions.removeLoading());
      }
    }
    return {
      status: errorEnd?.response?.status,
      message: errorEnd?.response?.message || "",
    };
  }

  public get<T>(
    endpoint: string,
    params?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<FetchResponse<T>> {
    return this.request<T>(endpoint, "GET", undefined, headers, params);
  }

  public post<T>(
    endpoint: string,
    body: any,
    headers?: Record<string, string>,
  ): Promise<FetchResponse<T>> {
    return this.request<T>(endpoint, "POST", body, headers);
  }

  public put<T>(
    endpoint: string,
    body: any,
    headers?: Record<string, string>,
  ): Promise<FetchResponse<T>> {
    return this.request<T>(endpoint, "PUT", body, headers);
  }

  public patch<T>(
    endpoint: string,
    body: any,
    headers?: Record<string, string>,
  ): Promise<FetchResponse<T>> {
    return this.request<T>(endpoint, "PATCH", body, headers);
  }

  public delete<T>(endpoint: string, headers?: Record<string, string>): Promise<FetchResponse<T>> {
    return this.request<T>(endpoint, "DELETE", undefined, headers);
  }
}

export default new ApiClient();
